import type { AppRouter } from '@app/api'
import {
  type BlockContent,
  type BookLanguages,
  type Call,
  ENGLISH,
  type Inline,
  type TextAnchor,
} from '@app/studio'
import type { inferRouterOutputs } from '@trpc/server'
import { createContext, type ReactNode } from 'react'
import type { GoTo } from './go-to'

export type Composed = inferRouterOutputs<AppRouter>['book']['compose']
export type Section = Composed[number]
export type Block = Section['blocks'][number]

/**
 * How a note is rendered wherever it is called.
 *
 * A context rather than three props: a note call can appear inside any inline
 * run at any depth, so threading them by hand would mean every block type
 * forwarding arguments it does not use, and a new block type forgetting to.
 */
export type NoteRendering = {
  /** Each note's body, already rendered. */
  bodies: Map<string, ReactNode>
  /** Each note's computed number. Computed, never stored. */
  markers: Map<string, string>
  /** Supplied while the pane is open — then a marker reveals it there. */
  onReveal?: (noteId: string) => void
}

export const Notes = createContext<NoteRendering>({
  bodies: new Map(),
  markers: new Map(),
})

/**
 * How a block's discussion is marked, wherever the block is drawn.
 *
 * A context for the same reason notes have one: every block kind would
 * otherwise forward three props it does not read, and a new kind would forget
 * to.
 */
export type CommentMarking = {
  /** Open threads on each block. A resolved one is not something outstanding. */
  counts: Map<string, number>
  /**
   * Take this passage to the band — the one place a comment is read or raised.
   *
   * One destination whether the block is already discussed or not, so a mark
   * never offers two ways to reach one conversation. What it does THERE follows
   * what the mark promised: a count promises a discussion to read, and a plus
   * promises somewhere to write.
   */
  onOpen?: (blockId: string, intent: 'read' | 'raise') => void
  /**
   * A thread reached by clicking its own words in the text.
   *
   * The marked phrase is the most direct thing a reader can point at, and until
   * it answered it was the one part of the link that only went one way.
   */
  onOpenThread?: (commentId: string, blockId: string) => void
  /** Whether a passage nobody has commented on is worth marking at all. */
  canRaise?: boolean
  /** The passage the band is showing, marked as the reader's own position. */
  revealed?: GoTo
  /**
   * The one thread the reader is on, whose words are marked more strongly.
   *
   * Where a paragraph carries several points, marking them all alike says the
   * paragraph is discussed and nothing about which words each point is about.
   */
  active?: string | null
  /**
   * The words each thread points at, drawn over the text they name.
   *
   * Keyed by passage, because an anchor is only ever searched for inside the
   * block it belongs to — which is what keeps finding it the cost of scanning a
   * paragraph rather than a book.
   */
  anchors?: Map<string, { id: string; anchor: TextAnchor }[]>
  /** Which of those found their words. What is missing from it is orphaned. */
  onAnchorsDrawn?: (found: Set<string>) => void
  /** Words the reader has selected, and where. Cleared when they let go. */
  onSelect?: (picked: { blockId: string; anchor: TextAnchor } | null) => void
  /** The passage holding that selection, so its mark offers to carry it. */
  picked?: string | null
}

export const Comments = createContext<CommentMarking>({ counts: new Map() })

/**
 * What the book is written in, wherever a run of it is drawn.
 *
 * A context for the same reason notes and comments have one: a run appears at
 * any depth inside any block kind, and threading the book down by hand would
 * mean every kind forwarding a prop it does not read. It is also what lets a
 * note's body — rendered outside the reader, in the band — set itself the same
 * way the text does.
 */
export const Book = createContext<BookLanguages>(ENGLISH)

export const BookProvider = Book.Provider

/**
 * Whether the reader is also a writing surface, and which passage is open.
 *
 * The reader is not replaced to write in it. A second rendering of the same
 * book would lose the reader's place, set the words in a face they are not
 * published in, and put the whole book into a state for the sake of one
 * sentence. So this rides alongside: the page, the scroll and the type are the
 * ones already on screen, and exactly one passage at a time is writable.
 *
 * `armed` rather than "editing", because clicking is how a reader SELECTS words
 * to remark on. Writing has to be asked for, or the two acts take the same
 * gesture and one of them stops working.
 */
export type Writing = {
  armed: boolean
  /** The passage open for writing. One, so nothing is unsaved out of sight. */
  openId?: string | null
  onOpen?: (blockId: string) => void
  /**
   * The draft of the open passage, and how it is changed.
   *
   * The calls travel with it. A marker says only that a call sits here, so an
   * edit that moves or deletes one has to carry what it named — otherwise the
   * save can only refuse, having no way to tell which of two notes went.
   */
  draft?: { content: BlockContent; calls: Call[] } | null
  onDraft?: (draft: { content: BlockContent; calls: Call[] }) => void
  /**
   * The commit for the open passage, drawn beside it.
   *
   * Passed down rather than rendered by the page, because it belongs to ONE
   * paragraph: at the foot of the book it is a commit nobody has scrolled to,
   * and it would say nothing about which passage it was for.
   */
  commit?: ReactNode
  /**
   * A heading open for writing, and its draft.
   *
   * A title is text somebody wrote, so it is edited where it is read like any
   * other — and it is held apart from a passage because it is a different row
   * with a different write. What a section IS, and where it starts on the page,
   * stays the owner's and is not reachable from here.
   */
  openSectionId?: string | null
  onOpenSection?: (sectionId: string) => void
  sectionDraft?: {
    title: InlineRunValue
    subtitle: InlineRunValue | null
    /** What the heading's markers name, carried so an edit can move them. */
    calls: Call[]
  } | null
  onSectionDraft?: (draft: {
    title: InlineRunValue
    subtitle: InlineRunValue | null
    calls: Call[]
  }) => void
  sectionCommit?: ReactNode
  /**
   * Committing and abandoning, so an open editor can be finished from the
   * keyboard. A surface reachable only by finding a button is not operable.
   */
  onCommit?: () => void
  onCancel?: () => void
}

/** The shape a title and a subtitle both are. */
type InlineRunValue = Inline[]

export const Writing = createContext<Writing>({ armed: false })

export const WritingProvider = Writing.Provider
